// frontend/src/pages/librarian/Dashboard.tsx
/* Hallmark · genre: editorial · macrostructure: Greeting → counters → flow + collection → desk
 * theme: house (styles/lib-theme.ts) · enrichment: none · nav/footer: inherited (either staff shell)
 * motion: colour transitions only · signature: the greeting banner's shelf of spines
 * system: one page for /librarian and /admin/overview; links come from useStaffPaths
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle, ArrowLeftRight, BarChart3, BookOpen, BookPlus, ChevronRight, Inbox, Undo2, UserPlus, Users,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardOverview } from '@/hooks/dashboard-hooks';
import { useReportsDashboard } from '@/hooks/reports-hooks';
import { useBookManagementAnalytics } from '@/hooks/book-management-hooks';
import { useCatalogOverview } from '@/hooks/catalog-hooks';
import { useStaffPaths, useStaffTopbar } from '@/contexts/StaffAreaContext';
import type { ActivityItem } from '@/services/dashboard-api';
import GreetingBanner from '@/components/librarian/dashboard/GreetingBanner';
import StatTile from '@/components/librarian/dashboard/StatTile';
import Panel, { PanelSkeleton } from '@/components/librarian/dashboard/Panel';
import AreaChart from '@/components/librarian/ui/charts/AreaChart';
import Monogram from '@/components/librarian/ui/Monogram';
import LibButton from '@/components/librarian/ui/LibButton';
import { formatDate, initialsOf, plural } from '@/lib/format';
import {
  greetingFor, money, quoteForDay, shareOfBooks, shortDay, todayLine,
} from '@/components/librarian/dashboard/dashboard-format';

type Range = 'week' | 'month';
const RANGES: Record<Range, { label: string; days: number }> = {
  week: { label: '7 days', days: 7 },
  month: { label: '30 days', days: 30 },
};

const linkCls = 'text-[var(--lib-ink)] underline-offset-4 hover:underline';

const Dashboard: React.FC = () => {
  useStaffTopbar({ title: 'Dashboard', subtitle: null });
  const paths = useStaffPaths();
  const admin = paths.base === '/admin';
  const { user } = useAuth();

  const overview = useDashboardOverview();
  const reports = useReportsDashboard();
  const catalog = useCatalogOverview();
  const [range, setRange] = useState<Range>('month');
  const analytics = useBookManagementAnalytics({
    date_range: range, include_trends: true, include_popular: true, limit: 5,
  });

  const s = overview.data?.statistics;
  const growth = reports.data?.overview.monthlyGrowth;
  const firstName: string =
    user?.first_name || overview.data?.user.name?.split(' ')[0] || (admin ? 'admin' : 'librarian');

  const trend = (analytics.data?.circulation_trends ?? []).map((t) => ({
    day: shortDay(t.date), issues: t.issues, returns: t.returns,
  }));
  const mostBorrowed = analytics.data?.popular_books?.[0];
  const topRated = catalog.data?.top_rated_books?.[0];
  const justAdded = catalog.data?.recent_books?.[0];
  const biggestGenre = reports.data?.books.byCategory?.[0];
  const activity: ActivityItem[] = (overview.data?.recent_activity ?? []).slice(0, 6);

  const quickActions = admin
    ? [
        { icon: BookPlus, title: 'Add a book', hint: 'Catalogue a new title and its copies', to: paths.bookNew() },
        { icon: UserPlus, title: 'Add a user', hint: 'Create a member or staff account', to: paths.memberNew() },
        { icon: ArrowLeftRight, title: 'Loans desk', hint: 'Issue, return and renew', to: paths.loans() },
        { icon: BarChart3, title: 'Reports', hint: 'Circulation and collection reports', to: `${paths.base}/reports` },
      ]
    : [
        { icon: BookOpen, title: 'Issue a book', hint: 'Lend a copy to a member', to: paths.loanNew() },
        { icon: Undo2, title: 'Return a book', hint: 'Find the loan and check the copy in', to: paths.loans({ filter: 'active' }) },
        { icon: BookPlus, title: 'Add a book', hint: 'Catalogue a new title and its copies', to: paths.bookNew() },
        { icon: UserPlus, title: 'Register a member', hint: 'Create a student account', to: paths.memberNew() },
      ];

  const desk: Array<{ label: string; value?: string | number; to?: string; tone?: 'danger' | 'gold' }> = [
    { label: 'Requests awaiting approval', value: s?.requests.pending, to: paths.loans({ filter: 'requests' }), tone: s && s.requests.pending > 0 ? 'gold' : undefined },
    { label: 'Issued today', value: s?.loans.issued_today, to: paths.loans({ filter: 'active' }) },
    { label: 'Returned today', value: s?.loans.returned_today, to: paths.loans({ filter: 'returned-today' }) },
    { label: 'Due back today', value: s?.loans.due_today, to: paths.loans({ filter: 'due-today' }) },
    { label: 'Overdue', value: s?.loans.overdue, to: paths.loans({ filter: 'overdue' }), tone: s && s.loans.overdue > 0 ? 'danger' : undefined },
    { label: 'Fines collected today', value: s ? money(s.fines.collected_today) : undefined, to: admin ? undefined : `${paths.base}/penalties` },
    ...(admin ? [] : [{ label: 'Open tasks', value: s?.tasks.pending, to: `${paths.base}/tasks` }]),
    { label: 'Unread notifications', value: s?.notifications.unread, to: `${paths.base}/notifications` },
  ];

  return (
    <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6">
      <GreetingBanner greeting={greetingFor()} name={firstName} line={todayLine(s)} quote={quoteForDay()} />

      {overview.isError && (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-lg border border-[var(--lib-danger)]/40 bg-[var(--lib-surface)] px-5 py-4">
          <p className="text-[13px] text-[var(--lib-ink)]">Today’s counters didn’t load.</p>
          <LibButton type="button" size="sm" variant="outline" onClick={() => void overview.refetch()}>
            Try again
          </LibButton>
        </div>
      )}

      <section aria-label="Library totals" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatTile
          label="Total books"
          value={s?.books.total}
          icon={BookOpen}
          delta={growth ? { value: growth.books, label: 'vs previous 30 days' } : undefined}
          caption={s ? `${plural(s.books.added_today, 'title', 'titles')} added today` : undefined}
          to={paths.books()}
        />
        <StatTile
          label="Members"
          value={s?.members.total}
          icon={Users}
          caption={s ? `${s.members.new_today.toLocaleString()} joined today` : undefined}
          to={paths.members()}
        />
        <StatTile
          label="Active loans"
          value={s?.loans.active}
          icon={ArrowLeftRight}
          delta={growth ? { value: growth.loans, label: 'issued vs previous 30 days' } : undefined}
          caption={s ? `${s.loans.issued_today.toLocaleString()} issued today` : undefined}
          to={paths.loans({ filter: 'active' })}
        />
        <StatTile
          label="Requests"
          value={s?.requests.pending}
          icon={Inbox}
          caption="awaiting approval"
          to={paths.loans({ filter: 'requests' })}
        />
        <StatTile
          label="Overdue"
          value={s?.loans.overdue}
          icon={AlertTriangle}
          tone="danger"
          caption={s ? `${s.loans.due_today.toLocaleString()} due back today` : undefined}
          to={paths.loans({ filter: 'overdue' })}
        />
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] items-start">
        <Panel
          title="Circulation"
          blurb={`Books issued and returned per day over the last ${RANGES[range].days} days.`}
          action={
            <div role="group" aria-label="Range" className="flex rounded-md border border-[var(--lib-hairline)] p-0.5">
              {(Object.keys(RANGES) as Range[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={range === r}
                  onClick={() => setRange(r)}
                  className={`h-7 px-2.5 rounded text-[12px] transition-colors ${
                    range === r
                      ? 'bg-[var(--lib-navy)] text-[var(--lib-sidebar)]'
                      : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)]'
                  }`}
                >
                  {RANGES[r].label}
                </button>
              ))}
            </div>
          }
        >
          <div className="px-5 pb-5">
            {analytics.isError ? (
              <div className="py-16 text-center space-y-3">
                <p className="text-[13px] text-[var(--lib-ink-muted)]">The circulation figures didn’t load.</p>
                <LibButton type="button" size="sm" variant="outline" onClick={() => void analytics.refetch()}>
                  Try again
                </LibButton>
              </div>
            ) : analytics.isLoading ? (
              <div aria-hidden className="h-[264px] rounded bg-[var(--lib-surface-2)] animate-pulse" />
            ) : (
              <AreaChart
                data={trend}
                xKey="day"
                series={[{ key: 'issues', label: 'Books issued' }, { key: 'returns', label: 'Books returned' }]}
                label={`Books issued and returned per day over the last ${RANGES[range].days} days`}
              />
            )}
          </div>
        </Panel>

        <Panel title="Collection" blurb="What the shelves are saying." action={{ label: 'View all', to: paths.books() }}>
          {analytics.isLoading && catalog.isLoading ? (
            <PanelSkeleton />
          ) : (
            <ul className="px-5 pb-2 divide-y divide-[var(--lib-hairline)]">
              <Highlight
                kind="Most borrowed"
                title={mostBorrowed?.book.title}
                to={mostBorrowed ? paths.books(mostBorrowed.book.id) : undefined}
                value={mostBorrowed ? mostBorrowed.circulation_count.toLocaleString() : undefined}
                unit={mostBorrowed ? plural(mostBorrowed.circulation_count, 'loan', 'loans').replace(/^\S+ /, '') : undefined}
              />
              <Highlight
                kind="Top rated"
                title={topRated?.title}
                sub={topRated?.authors?.map((a) => a.full_name).join(', ')}
                to={topRated ? paths.books(topRated.id) : undefined}
                value={topRated?.average_rating ? Number(topRated.average_rating).toFixed(1) : undefined}
                unit="average rating"
              />
              <Highlight
                kind="Just added"
                title={justAdded?.title}
                sub={justAdded?.authors?.map((a) => a.full_name).join(', ')}
                to={justAdded ? paths.books(justAdded.id) : undefined}
                value={justAdded ? formatDate(justAdded.created_at) : undefined}
                unit="added"
              />
              <Highlight
                kind="Biggest genre"
                title={biggestGenre?.category}
                to={biggestGenre ? paths.genres() : undefined}
                value={biggestGenre ? shareOfBooks(biggestGenre.count, reports.data?.overview.totalBooks) : undefined}
                unit={biggestGenre ? plural(biggestGenre.count, 'title', 'titles') : undefined}
                square
              />
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-[minmax(0,5fr)_minmax(0,3fr)_minmax(0,4fr)] items-start">
        <Panel title="Recent activity" blurb="Issues and returns in the last 24 hours." action={{ label: 'View all', to: paths.loans() }}>
          {overview.isLoading ? (
            <PanelSkeleton rows={5} />
          ) : activity.length === 0 ? (
            <div className="px-5 pb-6 pt-2 space-y-3">
              <p className="text-[13px] text-[var(--lib-ink-muted)]">Nothing has moved in the last 24 hours.</p>
              {!admin && (
                <Link to={paths.loanNew()} className="inline-block text-[13px] text-[var(--lib-gold-text)] underline-offset-4 hover:underline">
                  Issue a book
                </Link>
              )}
            </div>
          ) : (
            <ul className="px-5 pb-3 divide-y divide-[var(--lib-hairline)]">
              {activity.map((a) => {
                const returned = a.type === 'book_returned';
                const Icon = returned ? Undo2 : BookOpen;
                return (
                  <li key={a.id} className="flex items-center gap-3 py-3">
                    <span aria-hidden className="h-8 w-8 shrink-0 rounded-full bg-[var(--lib-gold)]/15 text-[var(--lib-gold-text)] inline-flex items-center justify-center">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <p className="min-w-0 flex-1 text-[13px] text-[var(--lib-ink)] truncate">
                      <span className="font-medium">{a.details?.member_name ?? a.user}</span>{' '}
                      {returned ? 'returned' : 'borrowed'}{' '}
                      <span className="font-serif italic">{a.details?.book_title}</span>
                    </p>
                    <time dateTime={a.timestamp} className="shrink-0 text-[11px] text-[var(--lib-ink-muted)] whitespace-nowrap">
                      {formatDistanceToNow(new Date(a.timestamp), { addSuffix: true })}
                    </time>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel title="Quick actions" blurb="The desk’s everyday jobs.">
          <ul className="grid grid-cols-2 gap-3 px-5 pb-5">
            {quickActions.map((q) => (
              <li key={q.title}>
                <Link
                  to={q.to}
                  className="flex h-full flex-col gap-2 rounded-md border border-[var(--lib-hairline)] p-3 transition-colors hover:border-[var(--lib-gold)] hover:bg-[var(--lib-surface-2)]"
                >
                  <span aria-hidden className="h-8 w-8 rounded-full bg-[var(--lib-gold)]/15 text-[var(--lib-gold-text)] inline-flex items-center justify-center">
                    <q.icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[13px] font-medium text-[var(--lib-ink)]">{q.title}</span>
                  <span className="text-[11px] leading-[1.5] text-[var(--lib-ink-muted)]">{q.hint}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Today at the desk" blurb="Live counts; each row opens its list.">
          <ul className="px-5 pb-3 divide-y divide-[var(--lib-hairline)]">
            {desk.map((row) => {
              const valueCls =
                row.tone === 'danger' ? 'text-[var(--lib-danger)]' : row.tone === 'gold' ? 'text-[var(--lib-gold-text)]' : 'text-[var(--lib-ink)]';
              const inner = (
                <>
                  <span className="min-w-0 flex-1 text-[13px] text-[var(--lib-ink)]">{row.label}</span>
                  <span className={`text-[13px] font-semibold tabular-nums ${valueCls}`}>
                    {row.value === undefined ? '—' : typeof row.value === 'number' ? row.value.toLocaleString() : row.value}
                  </span>
                  {row.to && <ChevronRight aria-hidden className="h-3.5 w-3.5 text-[var(--lib-ink-muted)]" />}
                </>
              );
              return (
                <li key={row.label}>
                  {row.to ? (
                    <Link to={row.to} className="flex items-center gap-3 py-3 hover:text-[var(--lib-gold-text)]">{inner}</Link>
                  ) : (
                    <div className="flex items-center gap-3 py-3">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </Panel>
      </div>
    </div>
  );
};

/** One collection highlight: what kind of record, which one, and the number that made it notable. */
const Highlight: React.FC<{
  kind: string;
  title?: string;
  sub?: string;
  to?: string;
  value?: string;
  unit?: string;
  square?: boolean;
}> = ({ kind, title, sub, to, value, unit, square }) => (
  <li className="flex items-center gap-3 py-3">
    <Monogram text={title ? initialsOf(title) : '—'} shape={square ? 'square' : 'round'} />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] text-[var(--lib-ink-muted)]">{kind}</p>
      {title ? (
        <p className="text-[13px] font-medium truncate">
          {to ? <Link to={to} className={linkCls}>{title}</Link> : title}
        </p>
      ) : (
        <p className="text-[13px] text-[var(--lib-ink-muted)]">Nothing yet</p>
      )}
      {sub && <p className="text-[11px] text-[var(--lib-ink-muted)] truncate">{sub}</p>}
    </div>
    {value && (
      <div className="shrink-0 text-right">
        <p className="text-[13px] font-semibold tabular-nums text-[var(--lib-ink)]">{value}</p>
        {unit && <p className="text-[11px] text-[var(--lib-ink-muted)]">{unit}</p>}
      </div>
    )}
  </li>
);

export default Dashboard;
